/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LoadingAi,
  Typography,
} from "../../components";
import {
  buildFiltrosBody,
  formatarDataChip,
  intervaloPreset,
  PERIODO_PRESETS,
} from "./painel-analise-fiscal";
import { PainelFiscalService, SessionService } from "../../services";
import { cacheApi, responseErros } from "../../utils";
import {
  PainelAnaliseFiscalDistribuicao,
  PainelAnaliseFiscalEstimativaTributos,
  PainelAnaliseFiscalFiltros,
  PainelAnaliseFiscalKpis,
  PainelAnaliseFiscalProdutos,
  PainelAnaliseFiscalDocumentosModelo,
} from "./components";
import { Skeleton } from "@mui/material";
import { useCacheContext } from "../../contexts/cache.context";
import { filtrosUrl } from "../../helpers";

const sessionService = new SessionService();
const painelFiscalService = new PainelFiscalService();

const resumoFiscalPadrao = {
  faturamentoNF: 0,
  estimativaTributos: [],
  semDocumentoFiscal: 0,
  notasAutorizadas: 0,
  notasRejeitadas: 0,
  notasCanceladas: 0,
  notasInutilizadas: 0,
  notasContingencia: 0,
  notasPendentes: 0,
  qtdNFe: 0,
  qtdNFCe: 0,
};

const analiseFiscalAiPadrao = {
  produtosAnalisados: 0,
  ncmsComInconsistencia: 0,
  gruposTributacaoIncorretos: 0,
  ncmsInconsistentes: [],
  gruposIncorretos: [],
};

const mensagemLoadingAI = [
  "Realizando análise dos dados de faturamento",
  "Sintetizando informações",
];

export default function PainelAnaliseFiscalView() {
  const { cacheDto, setCacheDto } = useCacheContext();
  const label = filtrosUrl.getUrl();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();
  const configuracoes = sessionService.getConfiguracoes();
  const acessosEmpresas = usuario?.acessosEmpresas || [];

  const filtrosPadrao = {
    ...intervaloPreset(PERIODO_PRESETS[3]),
    mesAno: new Date().toISOString().slice(0, 7),
    faturamentoMensal: configuracoes?.faturamentoMensal || 0,
    empresas: empresa ? [empresa] : [],
  };
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filtrosPadrao);
  const [filtros, setFiltros] = useState(filtrosPadrao);
  const [resumoFiscal, setResumoFiscal] = useState(resumoFiscalPadrao);
  const [analiseFiscalAi, setAnaliseFiscalAi] = useState(analiseFiscalAiPadrao);
  const [loading, setLoading] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const filtrosBody = useMemo(() => buildFiltrosBody(filtros), [filtros]);

  const activeCount =
    acessosEmpresas.length > 0 ? filtros.empresas?.length || 0 : 0;

  useEffect(() => {
    setLoading(true);
    setLoadingAI(true);
    Promise.all([buscarResumoFiscal(), buscarAnaliseFiscalAi()]).finally(() => {
      setLoading(false), setLoadingAI(false);
    });
  }, [filtrosBody]);

  async function buscarResumoFiscal() {
    const cacheBody = {
      dto: { cacheDto, setCacheDto },
      timeMinutes: 1000,
      apiFunction: painelFiscalService.getIndicadores({
        ...filtrosBody,
        faturamentoMensal: configuracoes?.faturamentoMensal || 0,
      }),
    };
    const result = await cacheApi(
      `${label}-${filtros.de}-${filtros.ate}-resumo-fiscal`,
      cacheBody,
    );
    if (!result.isAxiosError) {
      setResumoFiscal({ ...resumoFiscalPadrao, ...result.data });
    } else {
      responseErros(result);
    }
  }

  async function buscarAnaliseFiscalAi() {
    const cacheBody = {
      dto: { cacheDto, setCacheDto },
      timeMinutes: 1000,
      apiFunction: painelFiscalService.getProdutosAnaliseIa(filtrosBody),
    };
    const result = await cacheApi(
      `${label}-${filtros.de}-${filtros.ate}-analise-fiscal-ai`,
      cacheBody,
    );
    if (!result.isAxiosError) {
      setAnaliseFiscalAi({ ...analiseFiscalAiPadrao, ...result.data });
    } else {
      responseErros(result);
    }
  }

  const abrirFiltros = () => {
    setDraft(filtros);
    setOpen(true);
  };

  return (
    <Card className="minimal-layout-card">
      <CardContent className="minimal-layout-card-content rolagem">
        <Box sx={{ display: "flex", flexDirection: "column", pb: "24px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              pb: 3,
            }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Painel de Análise Fiscal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Indicativos fiscais gerados por IA a partir da base de produtos
                e vendas do cliente.
              </Typography>
            </Box>
            <Badge
              badgeContent={activeCount}
              color="primary"
              overlap="rectangular"
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={abrirFiltros}
                startIcon={<i className="ph-fill ph-sliders-horizontal" />}
                sx={{ whiteSpace: "nowrap" }}
              >
                Filtros
              </Button>
            </Badge>
          </Box>
          <PainelAnaliseFiscalFiltros
            open={open}
            setOpen={setOpen}
            filtros={filtros}
            filtrosPadrao={filtrosPadrao}
            acessosEmpresas={acessosEmpresas}
            onAplicar={setFiltros}
            draft={draft}
            setDraft={setDraft}
          />
        </Box>
        <PainelAnaliseFiscalKpis
          resumoFiscal={resumoFiscal}
          filtros={filtros}
          loading={loading}
        />
        <Grid container spacing={2} alignItems="stretch" sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <PainelAnaliseFiscalDistribuicao
              loading={loading}
              resumoFiscal={resumoFiscal}
            />
          </Grid>
          <Grid item xs={12}>
            <PainelAnaliseFiscalDocumentosModelo
              loading={loading}
              resumoFiscal={resumoFiscal}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="stretch" sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <PainelAnaliseFiscalEstimativaTributos
              resumoFiscal={resumoFiscal}
              estimativaTributos={resumoFiscal.estimativaTributos}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <PainelAnaliseFiscalProdutos
              analiseFiscalAi={analiseFiscalAi}
              loading={loading}
            />
          </Grid>
        </Grid>
      </CardContent>
      <LoadingAi loading={loadingAI} mensagens={mensagemLoadingAI} />
    </Card>
  );
}
